import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface DepartmentWorkshopDistributionChartProps {
  data: Array<{
    department: string;
    workshop_count: number;
    percentage: number;
    completion_rate: number;
  }>;
  loading?: boolean;
}

const DepartmentWorkshopDistributionChart: React.FC<DepartmentWorkshopDistributionChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Workshop Distribution</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Workshop Distribution</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No distribution data available
        </div>
      </div>
    );
  }

  const colors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
  ];

  const getDistributionLevel = (percentage: number) => {
    if (percentage >= 30) return { level: 'High', color: '#ef4444' };
    if (percentage >= 20) return { level: 'Medium', color: '#f59e0b' };
    if (percentage >= 10) return { level: 'Normal', color: '#3b82f6' };
    return { level: 'Low', color: '#10b981' };
  };

  const totalWorkshops = data.reduce((sum, item) => sum + item.workshop_count, 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Workshop Distribution</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ department, percentage }: any) => `${department}: ${(percentage as number).toFixed(1)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="workshop_count"
              nameKey="department"
            >
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={colors[index % colors.length]} 
                />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value: number, name: string, props: any) => [
                `${value} workshops (${props.payload.percentage.toFixed(1)}%)`,
                'Workshop Count'
              ]}
              labelFormatter={(label) => `Department: ${label}`}
            />
            <Legend 
              verticalAlign="bottom" 
              height={36}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Distribution Breakdown</h4>
          <div className="space-y-2">
            {[...data]
              .sort((a, b) => b.workshop_count - a.workshop_count)
              .map((item, index) => {
                const distribution = getDistributionLevel(item.percentage);
                return (
                  <div key={item.department} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded mr-2" 
                        style={{ backgroundColor: colors[index % colors.length] }}
                      ></div>
                      <span className="text-sm font-medium">{item.department}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{item.workshop_count}</span> workshops
                      <span className={`ml-2 text-xs px-2 py-1 rounded`} style={{ 
                        backgroundColor: distribution.color + '20', 
                        color: distribution.color 
                      }}>
                        {item.percentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Workload Analysis</h4>
          <div className="space-y-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Most Active</span>
                <span className="font-bold text-blue-600">
                  {data.reduce((max, item) => item.workshop_count > max.workshop_count ? item : max, data[0])?.department}
                </span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                {Math.max(...data.map(d => d.workshop_count))} workshops
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Best Completion</span>
                <span className="font-bold text-green-600">
                  {data.reduce((max, item) => item.completion_rate > max.completion_rate ? item : max, data[0])?.department}
                </span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                {Math.max(...data.map(d => d.completion_rate)).toFixed(1)}% rate
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Workshops</span>
                <span className="font-bold text-gray-900">{totalWorkshops}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Across {data.length} departments
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentWorkshopDistributionChart;
